import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {useQuery} from "@khan/data-gql";
import {useLocation, type Location} from "@khan/urls";

import useProfile from "#data-access/use-profile.ts";
import {BIRTH_MONTH_YEAR_QUERY} from "#page-package/wham-link.tsx";
import {useNewRegistrationFlowFeature} from "#shared/auth/utils/use-new-registration-flow-feature.ts";

export const FEATURE_START_DATE = new Date("2025-01-23T00:00:00");

/**
 * Used to determine if we should force the user to fill out extra information.
 * This is part of the new user registration flow.
 */
export function useForceExtraInfo(): boolean {
    const location = useLocation();
    const inExperiment = useNewRegistrationFlowFeature();
    const {profile} = useProfile();
    const [userBirthMonthYear] = useQuery(BIRTH_MONTH_YEAR_QUERY, {
        skip: !profile,
        fetchPolicy: FetchPolicy.NetworkOnly,
    });

    // First ensure the user is in the experiment && has a profile
    if (
        // Not in the experiment
        !inExperiment ||
        // Not already on a page with /extra-info in the path
        location.pathname.match(/\/extra-info/) ||
        // No profile loaded yet
        !profile ||
        // BirthMonthAnd year has not successfully loaded
        userBirthMonthYear.status !== "success" ||
        // User is logged out so no birthMonthYear is available ||
        !userBirthMonthYear.data.user
    ) {
        return false;
    }

    // Ensure they joined after the feature launched
    const joined = new Date(profile.get("joined"));

    if (!isAfter(joined, FEATURE_START_DATE)) {
        return false;
    }

    // District teachers do not need to fill out extra information
    if (profile.get("isTeacher") && profile.get("userDistrictInfos")) {
        return false;
    }

    // Parents or teachers need to have entered their nickname and
    // birthday to be considered complete
    if (profile.get("isParent") || profile.get("isTeacher")) {
        return (
            !profile.get("nickname") ||
            (userBirthMonthYear.status === "success" &&
                !userBirthMonthYear.data.user.birthMonthYear)
        );
    }

    // Otherwise, if they are a learner, over the age gate, they need to have
    // a nickname
    if (!profile.get("underAgeGate")) {
        return !profile.get("nickname");
    }

    // Learners under the age gate require no additional information

    return false;
}

/**
 * Determines if the testDate is after the compareDate.
 */
function isAfter(testDate: Date, compareDate: Date): boolean {
    return testDate > compareDate;
}

/**
 * Returns the redirect path for the user to fill out their extra information,
 * with a "continue" query parameter to redirect them back to the page they
 * were on.
 *
 * For new /writing-coach pages, this will use the new sign up flow.
 */
export function getExtraInfoRedirectPath(location: Location): string {
    let redirectPath = `/signup/extra-info?continue=${encodeURIComponent(location.pathname + location.search)}`;

    if (location.pathname.match(/^\/writing-coach/i)) {
        redirectPath = "/writing-coach" + redirectPath;
    }

    return redirectPath;
}
